/**
 * Custom hook para manejar el estado de la vista Forecast Metrics
 * Centraliza toda la lógica de estado y event handlers
 */

import { useState, useEffect } from 'react';
import { 
  FORECAST_METRICS_DEFAULT_VALUES,
  getForecastRightLayer,
  getForecastModelDisplayName,
  getForecastMetricsName
} from '../config/forecastMetrics.js';
import {
  getYesterdayFormatted,
  calculateForecastDateRange,
  getForecastWeekNumber,
  formatForecastDate,
  combineForecastDateTime
} from '../utils/forecastMetricsDateUtils.js';

/**
 * Hook personalizado para gestionar el estado de Forecast Metrics
 * @returns {Object} Estado y funciones para manejar la vista
 */
export const useForecastMetricsState = () => {
  // Estados principales
  const [cqlSevere, setCqlSevere] = useState(FORECAST_METRICS_DEFAULT_VALUES.cqlSevere);
  const [cqlFecha, setCqlFecha] = useState(getYesterdayFormatted());
  const [selectedModel, setSelectedModel] = useState(FORECAST_METRICS_DEFAULT_VALUES?.selectedModel || 'CNN_HISTORICAL:EU_025_live_demo');
  const [selectedRisk, setSelectedRisk] = useState(FORECAST_METRICS_DEFAULT_VALUES.selectedRisk);
  const [tabIndex, setTabIndex] = useState(FORECAST_METRICS_DEFAULT_VALUES.tabIndex);

  // Estados de capas de mapas
  const [mapLeftLayer, setMapLeftLayer] = useState(FORECAST_METRICS_DEFAULT_VALUES.selectedModel);
  const [mapRightLayer, setMapRightLayer] = useState('TARGETS_CNN:lightnings_EU_025');
  const [mapCrossborderLayer, setMapCrossborderLayer] = useState(null);

  // Estados de tiempo y fecha
  const [issueTime, setIssueTime] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [issueTimeOptions, setIssueTimeOptions] = useState([]);

  // Estados de UI
  const [anchorEl, setAnchorEl] = useState(null);
  const [backdropOpen, setBackdropOpen] = useState(false);

  // Valores calculados
  const dateRange = calculateForecastDateRange(cqlFecha);
  const weekNumber = getForecastWeekNumber(cqlFecha);
  const prettyModelName = getForecastModelDisplayName(selectedModel);
  const metricsName = getForecastMetricsName(selectedModel);

  // Efecto para actualizar la capa derecha cuando cambia la izquierda
  useEffect(() => {
    const rightLayer = getForecastRightLayer(mapLeftLayer);
    setMapRightLayer(rightLayer);
  }, [mapLeftLayer, cqlSevere, weekNumber]);

  // Efecto para combinar fecha y hora seleccionadas
  useEffect(() => {
    if (selectedDate && selectedTime) {
      const combinedDateTime = combineForecastDateTime(selectedDate, selectedTime);
      setIssueTime(combinedDateTime);
    }
  }, [selectedDate, selectedTime]);

  // Event handlers
  const handleDateChange = (fecha) => {
    const formattedDate = formatForecastDate(fecha);
    setCqlFecha(formattedDate);
  };

  const handleModelClick = (value) => {
    setSelectedModel(value);
    setMapLeftLayer(value);
  };

  const handleRiskClick = (value) => {
    setSelectedRisk(value);
    setCqlSevere(value);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleFilterChange = (event, value) => {
    setCqlSevere(value || 'Light');
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Handlers para backdrop
  const handleBackdropOpen = () => {
    setBackdropOpen(true);
  };

  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };

  // Handlers para menú
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const selectorCapasDuales = (layerName) => {
    setMapLeftLayer(layerName);
    handleClose();
  };

  return {
    // Estados
    cqlSevere,
    cqlFecha,
    selectedModel,
    selectedRisk,
    tabIndex,
    mapLeftLayer,
    mapRightLayer,
    mapCrossborderLayer,
    issueTime,
    selectedDate,
    selectedTime,
    issueTimeOptions,
    anchorEl,
    backdropOpen,
    
    // Valores calculados
    cqlFechaMin: dateRange.cqlFechaMin,
    cqlFechaMax: dateRange.cqlFechaMax,
    weekNumber,
    prettyModelName,
    metricsName,
    
    // Event handlers
    handleDateChange,
    handleModelClick,
    handleRiskClick,
    handleDateSelect,
    handleTimeSelect,
    handleFilterChange,
    handleTabChange,
    handleBackdropOpen,
    handleBackdropClose,
    handleClick,
    handleClose,
    selectorCapasDuales,
    
    // Setters para casos especiales
    setIssueTimeOptions,
    setCqlSevere,
    setSelectedModel,
    setSelectedRisk,
    setTabIndex,
  };
};
